import React, { memo } from "react";
import { Text, View, TouchableOpacity } from "react-native";
import { Image } from "expo-image";
import Svg, { Path, Circle } from "react-native-svg";
import { StyleSheet } from "@/utils/appStyleSheet";
import { FontFamilies } from "@/constants/Typography";
import { Shadows } from "@/constants/Shadows";
import { formatCurrency } from "@/utils/myMoney";
import { router } from "expo-router";
import {
  UserFinanceDataResponse,
  Subscription,
  BillingCadence,
} from "@/types/financeData";
import { useBrandLogo } from "@/hooks/useBrandLogo";

interface SubscriptionsCardProps {
  financeData: UserFinanceDataResponse | null;
}

// Format billing cadence to display text
const formatCadence = (cadence: BillingCadence): string => {
  switch (cadence) {
    case "weekly":
      return "Every week";
    case "biweekly":
      return "Every 2 weeks";
    case "monthly":
      return "Every month";
    case "quarterly":
      return "Every 3 months";
    case "semiannual":
      return "Every 6 months";
    case "annual":
      return "Every year";
    default:
      return "Recurring";
  }
};

// Format date to display (e.g., "Dec 8")
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

// Get initial letter for fallback logo
const getInitialLetter = (name: string): string => {
  return name.charAt(0).toUpperCase();
};

// Subscription row item - uses hook for brand logo
const SubscriptionItem = ({
  subscription,
  isLast,
}: {
  subscription: Subscription;
  isLast: boolean;
}) => {
  const { icon } = useBrandLogo(subscription.merchantName);

  return (
    <View>
      <View style={styles.subscriptionRow}>
        {/* Logo */}
        <View style={styles.logoContainer}>
          {icon ? (
            <Image source={{ uri: icon }} style={styles.logo} />
          ) : (
            <View style={styles.logoFallback}>
              <Text style={styles.logoInitial}>
                {getInitialLetter(subscription.merchantName)}
              </Text>
            </View>
          )}
        </View>

        {/* Name and cadence */}
        <View style={styles.subscriptionInfo}>
          <Text style={styles.merchantName} numberOfLines={1}>
            {subscription.merchantName}
          </Text>
          <Text style={styles.cadence}>
            {formatCadence(subscription.billingCadence)}
          </Text>
        </View>

        {/* Amount and date */}
        <View style={styles.amountContainer}>
          <Text style={styles.amount}>
            {formatCurrency(subscription.amount)}
          </Text>
          <Text style={styles.date}>
            {formatDate(subscription.firstTransactionAt)}
          </Text>
        </View>
      </View>

      {/* Divider */}
      {!isLast && <View style={styles.divider} />}
    </View>
  );
};

export default memo(function SubscriptionsCard({
  financeData,
}: SubscriptionsCardProps) {
  const allSubscriptions = financeData?.analysis?.spending?.subscriptions;

  // Show empty state if no subscriptions
  if (!allSubscriptions?.length) {
    return (
      <View style={styles.card}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.title}>Recurring Subscriptions</Text>
          </View>
        </View>
        <View style={styles.headerDivider} />
        <View style={styles.emptyState}>
          <Text style={styles.emptyTitle}>No subscriptions found</Text>
          <Text style={styles.emptySubtitle}>
            We&apos;ll detect your recurring charges automatically
          </Text>
        </View>
      </View>
    );
  }

  const subscriptions = allSubscriptions.slice(0, 7);
  const remainingCount = Math.max(0, allSubscriptions.length - 7);

  const handleViewMore = () => {
    router.push("/(tabs)/my-money/spending");
  };

  return (
    <View style={styles.card}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text style={styles.title}>Recurring Subscriptions</Text>
        </View>
      </View>

      {/* Divider */}
      <View style={styles.headerDivider} />

      {/* Subscriptions list */}
      <View style={styles.subscriptionsList}>
        {subscriptions.map((subscription, index) => (
          <SubscriptionItem
            key={subscription.merchantName + index}
            subscription={subscription}
            isLast={index === subscriptions.length - 1}
          />
        ))}
      </View>

      {/* View more button */}
      {remainingCount > 0 && (
        <TouchableOpacity
          onPress={handleViewMore}
          style={styles.viewMoreButton}
        >
          <Text style={styles.viewMoreText}>
            View more {remainingCount} upcoming
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 20,
    ...Shadows.card,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 8,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  title: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
  },
  headerDivider: {
    height: 1,
    backgroundColor: "#EBEBEB",
    marginBottom: 8,
  },
  subscriptionsList: {
    gap: 0,
  },
  subscriptionRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    gap: 12,
  },
  logoContainer: {
    width: 40,
    height: 40,
  },
  logo: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  logoFallback: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#F3F4F6",
    alignItems: "center",
    justifyContent: "center",
  },
  logoInitial: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  subscriptionInfo: {
    flex: 1,
    gap: 2,
  },
  merchantName: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  cadence: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    opacity: 0.5,
  },
  amountContainer: {
    alignItems: "flex-end",
    gap: 2,
  },
  amount: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  date: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    opacity: 0.5,
  },
  divider: {
    height: 1,
    backgroundColor: "#F3F3F3",
  },
  viewMoreButton: {
    marginTop: 8,
    paddingHorizontal: 24,
    paddingVertical: 8,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: "#A3A3A3",
    alignSelf: "center",
  },
  viewMoreText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    textAlign: "center",
  },
  emptyState: {
    alignItems: "center",
    paddingVertical: 24,
    gap: 8,
  },
  emptyTitle: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  emptySubtitle: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#A3A3A3",
    textAlign: "center",
  },
});
